import { jwtDecode } from 'jwt-decode';
import PropTypes from 'prop-types';
import type { FC } from 'react';
import { createContext, useEffect, useReducer } from 'react';
import toast from 'react-hot-toast';
import logger from 'src/utils/logger';
import { authApi } from '../api/authApi';
import { refreshAuthSession, resetRefreshSessionFlight } from '../utils/authRefresh';
import { invalidateUserProfileLoader } from 'src/api/userProfileLoader';
import {
  clearAuthSessionStorage,
  extractAuthTokens,
  persistAuthTokens,
} from '../utils/authTokens';
import { resetUnauthorizedToastFlag } from '../utils/axios/index';
import { store } from '../store/store';
import { resetSecurityFilters } from '../store/slices/securityFiltersSlice';
import { Action, AuthContextValue, AuthProviderProps, InitializeAction, IsFirstTimeAction, LoginAction, RegisterAction, State } from './JWTContext-types';

const initialState: State = {
  isFirstTime: false,
  isAuthenticated: false,
  isInitialized: false,
  is2FAEnabled: false,
  isOtpVerified: false,
  user: null,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const handlers: Record<string, (state: State, action: Action) => State> = {
  INITIALIZE: (state: State, action: InitializeAction): State => {
    const { isAuthenticated, user } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,

    };
  },
  LOGIN: (state: State, action: LoginAction): State => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      isInitialized: true,
      user,
    };
  },
  IS_FIRST_TIME: (state: State, action: IsFirstTimeAction): State => {
    const { isFirstTime } = action.payload;
    return { ...state, isFirstTime };
  },
  LOGOUT: (state: State): State => ({
    ...state,
    isAuthenticated: false,
    isInitialized: true,
    user: null,
  }),
  REGISTER: (state: State, action: RegisterAction): State => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  // SET_IS_2FA_ENABLED: (state: State, action: SetIs2FAEnabledAction): State => {
  //   const { is2FAEnabled } = action.payload;
  //   return { ...state, is2FAEnabled };
  // },
  // SET_IS_OTP_VERIFIED: (state: State, action: SetIsOtpVerifiedAction): State => {
  //   const { isOtpVerified } = action.payload;
  //   return { ...state, isOtpVerified };
  // },
};

const reducer = (state: State, action: Action): State => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext<AuthContextValue>({
  ...initialState,
  platform: 'JWT',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  resetIsFirstTime: () => Promise.resolve(),
  refreshUser: () => Promise.resolve(),
  verifyTwoFactorCode: () => Promise.resolve(),
});

const isValidToken = (accessToken: string | null): boolean => {
  if (!accessToken) {
    return false;
  }

  try {
    const currentTime = Date.now() / 1000;
    const decoded: { exp?: number } = jwtDecode(accessToken);

    if ((decoded?.exp ?? 0) <= currentTime) {
      localStorage.removeItem('accessToken');
      return false;
    }

    return true;
  } catch {
    localStorage.removeItem('accessToken');
    return false;
  }
};

export const setSession = (data: unknown) => {
  if (!data) {
    clearAuthSessionStorage();
    return;
  }

  const tokens = extractAuthTokens(data);
  if (tokens) {
    persistAuthTokens(tokens);
  }
};

export const AuthProvider: FC<AuthProviderProps> = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);
  useEffect(() => {
    const initialize = async (): Promise<void> => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');
        const refreshToken = window.localStorage.getItem('refreshToken');

        if (isValidToken(accessToken)) {
          const user = await authApi.me(accessToken);
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user,
            },
          });
        } else if (refreshToken && !isValidToken(accessToken)) {
          try {
            const tokens = await refreshAuthSession();
            const user = await authApi.me(tokens.accessToken);
            dispatch({
              type: 'INITIALIZE',
              payload: {
                isAuthenticated: true,
                user,
              },
            });
          } catch (error) {
            logger(error, '', 'errorss');
            toast.error('Session expired, Please login again!');
            resetRefreshSessionFlight();
            clearAuthSessionStorage();
            dispatch({
              type: 'INITIALIZE',
              payload: {
                isAuthenticated: false,
                user: null,
              },
            });
          }
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
          localStorage.removeItem('white_label');
        }
      } catch (err) {
        console.error(err);
        resetRefreshSessionFlight();
        clearAuthSessionStorage();
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialize();
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      // Step 1: Attempt to authenticate the user with email and password and get the login data
      const loginApiData = await authApi.login({ email, password });


      const { is2FAEnabled, qrCode, isQrVerified } = loginApiData;

      // Step 2: Check if 2FA is enabled
      if (is2FAEnabled) {
        localStorage.setItem('is2FAEnabled', 'true');
        // dispatch({
        //   type: 'SET_IS_2FA_ENABLED',
        //   payload: {
        //     is2FAEnabled: true,
        //   },
        // });

        // Step 3: Set the isOtpVerified to false
        localStorage.setItem('isOtpVerified', 'false');
        localStorage.setItem('qrCode', qrCode);
        localStorage.setItem('isQrVerified', isQrVerified.toString());
        // dispatch({
        //   type: 'SET_IS_OTP_VERIFIED',
        //   payload: {
        //     isOtpVerified: false,
        //   },
        // });

        // Step 4: Redirect to the 2FA page
        window.location.href = '/login/two-factor-authentication';
        return;
      }

      // if (!loginApiData) {
      //   // Handle authentication failure
      //   return;
      // }

      // Step 2: Set the user session and store the access token
      // setSession(loginApiData);

      // Step 3: Fetch user data
      let loggedUser;
      if (loginApiData.accessToken) {
        setSession(loginApiData);
        try {
          loggedUser = await authApi.me(loginApiData.accessToken);
          // Step 4: Dispatch user data after successful login
          dispatch({
            type: 'LOGIN',
            payload: {
              user: loggedUser,
            },
          });
        } catch (error) {
          if (error?.response?.status === 403) {
            // Handle unauthorized access
            window.location.href = '/reset-password';
            // localStorage.clear();
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.setItem('accessTokenForReset', loginApiData.accessToken);
            dispatch({
              type: 'IS_FIRST_TIME',
              payload: {
                isFirstTime: true,
              },
            });
          }
          else { localStorage.clear(); }
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.message);
      localStorage.clear();
    }
  };

  const verifyTwoFactorCode = async ({ email, password, otp }): Promise<void> => {
    try {
      const twoFactorApiResponse = await authApi.verifyTwoFactorCode({ email, password, otp });

      let loggedUser;
      if (twoFactorApiResponse.accessToken) {
        setSession(twoFactorApiResponse);

        try {
          loggedUser = await authApi.me(twoFactorApiResponse.accessToken);

          localStorage.removeItem('is2FAEnabled');
          localStorage.removeItem('isOtpVerified');
          localStorage.removeItem('isQrVerified');
          localStorage.removeItem('_te');
          localStorage.removeItem('_tp');
          localStorage.removeItem('qrCode');
          // Step 4: Dispatch user data after successful login
          dispatch({
            type: 'LOGIN',
            payload: {
              user: loggedUser,
            },
          });
        } catch (error) {
          if (error?.response?.status === 403) {
            // Handle unauthorized access (first-time login / password reset flow via 2FA)
            window.location.href = '/reset-password';
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.setItem('accessTokenForReset', twoFactorApiResponse.accessToken);
            dispatch({
              type: 'IS_FIRST_TIME',
              payload: {
                isFirstTime: true,
              },
            });
          } else {
            localStorage.clear();
          }
        }
      }
    } catch (error) {
      toast.error("Invalid verification code");
      // localStorage.clear();
    }
  }

  const logout = async (): Promise<void> => {
    resetRefreshSessionFlight();
    clearAuthSessionStorage();
    resetUnauthorizedToastFlag();
    store.dispatch(resetSecurityFilters());
    dispatch({ type: 'LOGOUT' });
  };

  const register = async (): Promise<void> => {
    await authApi.register();
  };

  const resetIsFirstTime = async (): Promise<void> => {
    dispatch({
      type: 'IS_FIRST_TIME',
      payload: {
        isFirstTime: false,
      }
    });
  };

  const refreshUser = async (): Promise<void> => {
    let accessToken = window.localStorage.getItem('accessToken');

    if (!isValidToken(accessToken)) {
      const refreshToken = window.localStorage.getItem('refreshToken');
      if (!refreshToken) {
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
        return;
      }

      try {
        const tokens = await refreshAuthSession();
        accessToken = tokens.accessToken;
      } catch (error) {
        logger(error, '', 'refreshUser');
        clearAuthSessionStorage();
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
        return;
      }
    }

    invalidateUserProfileLoader();
    const latestUser = await authApi.me(accessToken);
    dispatch({
      type: 'LOGIN',
      payload: {
        user: latestUser,
      },
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        platform: 'JWT',
        login,
        logout,
        register,
        resetIsFirstTime,
        refreshUser,
        verifyTwoFactorCode
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthContext;
